import { useState } from "react";

export interface UserCementJob {
  id: string;
  job_type: string;        // "squeeze", "cibp_cap", "primary", "plug", "remedial"
  cement_top_ft: number | null;    // Top of cement column (ft)
  cement_bottom_ft: number | null; // Bottom of cement column (ft)
  sacks: number | null;
  description: string;
}

interface CementJobEntryFormProps {
  cementJobs: UserCementJob[];
  onAdd: (job: UserCementJob) => void;
  onRemove: (id: string) => void;
}

const JOB_TYPES = [
  { value: "cibp_cap", label: "CIBP Cement Cap" },
  { value: "squeeze", label: "Squeeze Job" },
  { value: "primary", label: "Primary Cement" },
  { value: "plug", label: "Cement Plug" },
  { value: "remedial", label: "Remedial Cement" },
];

const JOB_TYPE_LABEL_MAP: Record<string, string> = {
  cibp_cap: "CIBP Cement Cap",
  squeeze: "Squeeze Job",
  primary: "Primary Cement",
  plug: "Cement Plug",
  remedial: "Remedial Cement",
};

export default function CementJobEntryForm({
  cementJobs,
  onAdd,
  onRemove,
}: CementJobEntryFormProps) {
  const [newJobType, setNewJobType] = useState("cibp_cap");
  const [newCementTop, setNewCementTop] = useState("");
  const [newCementBottom, setNewCementBottom] = useState("");
  const [newSacks, setNewSacks] = useState("");
  const [newDescription, setNewDescription] = useState("");

  const handleAdd = () => {
    const newJob: UserCementJob = {
      id: `user_cement_${Date.now()}`,
      job_type: newJobType,
      cement_top_ft: newCementTop ? parseFloat(newCementTop) : null,
      cement_bottom_ft: newCementBottom ? parseFloat(newCementBottom) : null,
      sacks: newSacks ? parseFloat(newSacks) : null,
      description: newDescription.trim(),
    };

    onAdd(newJob);

    // Reset form
    setNewJobType("cibp_cap");
    setNewCementTop("");
    setNewCementBottom("");
    setNewSacks("");
    setNewDescription("");
  };

  return (
    <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <h4 className="text-sm font-bold text-blue-900 dark:text-blue-100">
          Historic Cement Jobs
        </h4>
      </div>
      <p className="text-xs text-blue-800 dark:text-blue-200 mb-4">
        Enter any existing cement work in this well (squeeze jobs, CIBP caps, cement plugs). Specify where cement exists by top and bottom depth.
      </p>

      {/* Added Cement Jobs */}
      {cementJobs.length > 0 && (
        <div className="space-y-3 mb-4">
          <h5 className="text-xs font-bold text-blue-900 dark:text-blue-100 uppercase">
            Added Cement Jobs
          </h5>
          {cementJobs.map((job) => (
            <div key={job.id} className="bg-white dark:bg-gray-800 border-2 border-brand-300 dark:border-brand-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
                    {JOB_TYPE_LABEL_MAP[job.job_type] || job.job_type}
                    {(job.cement_top_ft !== null || job.cement_bottom_ft !== null) && (
                      <span className="font-normal text-gray-600 dark:text-gray-300">
                        {" — Cement: "}{job.cement_top_ft ?? "?"}-{job.cement_bottom_ft ?? "?"} ft
                      </span>
                    )}
                    {job.sacks !== null && (
                      <span className="font-normal text-gray-600 dark:text-gray-300">
                        {", "}{job.sacks} sks
                      </span>
                    )}
                  </p>
                  {job.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{job.description}</p>
                  )}
                  <p className="text-xs text-brand-600 dark:text-brand-400 font-medium mt-1">
                    User Added
                  </p>
                </div>
                <button
                  onClick={() => onRemove(job.id)}
                  className="ml-3 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Remove cement job"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Cement Job Form */}
      <div className="pt-4 border-t border-blue-300 dark:border-blue-700">
        <h5 className="text-xs font-bold text-blue-900 dark:text-blue-100 uppercase mb-3">
          Add Cement Job
        </h5>
        <div className="bg-white dark:bg-gray-800 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Job Type
            </label>
            <select
              value={newJobType}
              onChange={(e) => setNewJobType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              {JOB_TYPES.map((jt) => (
                <option key={jt.value} value={jt.value}>
                  {jt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Cement Top (ft)
              </label>
              <input
                type="number"
                value={newCementTop}
                onChange={(e) => setNewCementTop(e.target.value)}
                placeholder="e.g., 3800"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Shallowest depth of cement
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Cement Bottom (ft)
              </label>
              <input
                type="number"
                value={newCementBottom}
                onChange={(e) => setNewCementBottom(e.target.value)}
                placeholder="e.g., 5000"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Deepest depth of cement
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sacks (optional)
              </label>
              <input
                type="number"
                value={newSacks}
                onChange={(e) => setNewSacks(e.target.value)}
                placeholder="e.g., 25"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder={`e.g., Squeeze behind 7" casing at 5000'`}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <button
            onClick={handleAdd}
            className="w-full px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Cement Job
          </button>
        </div>
      </div>

      {/* Empty State */}
      {cementJobs.length === 0 && (
        <div className="text-center py-4 text-blue-700 dark:text-blue-300">
          <p className="text-sm">No cement jobs added yet.</p>
          <p className="text-xs mt-1">Add historic cement work above so the plan can account for existing isolation.</p>
        </div>
      )}
    </div>
  );
}
